import "@liveblocks/react-ui/styles.css"

import { LiveObject, type LsonObject, type Room } from "@liveblocks/client"
import {
  type JsonObject,
  LiveblocksProvider,
  RoomContext as LiveblocksRoomContext,
  useClient,
  useMutation,
  useStorage,
} from "@liveblocks/react"
import type { Document as DocumentT } from "@prismicio/types-internal/lib/content"
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"

import { useCollaborationRoomFlag } from "../../app/integrations/AmplitudeExperiments"
import { getRepositoryName } from "../../app/routing"
import {
  authenticateLiveblocks,
  resolveMentionSuggestions,
  resolveUsers,
} from "../../app/services/liveblocks"
import { useRepository } from "../../app/services/repository"
import type { ExistingDocumentData } from "../documentData"

export function CollaborationRoom(props: PropsWithChildren) {
  const { children } = props

  const { authors } = useRepository()

  if (useCollaborationRoomFlag() === "off") {
    return <>{children}</>
  }

  return (
    <LiveblocksProvider
      authEndpoint={authenticateLiveblocks}
      resolveUsers={({ userIds }) => resolveUsers({ userIds, authors })}
      resolveMentionSuggestions={({ text }) =>
        resolveMentionSuggestions({ text, authors })
      }
    >
      <CollaborationRoomInner>{children}</CollaborationRoomInner>
    </LiveblocksProvider>
  )
}

// We split CollaborationRoom and CollaborationRoomInner so the latter can call useClient with a matching LiveblocksProvider parent.
function CollaborationRoomInner(props: PropsWithChildren) {
  const { children } = props

  const client = useClient()
  const [roomState, setRoomState] = useState<RoomState>()

  const init = useCallback(
    (initData: ExistingDocumentData) => {
      if (roomState) return roomState.room

      const { meta } = initData

      // We only scope the room to the document ID and not the version ID.
      // This is because we want to be able to see the comments when going from one version to another.
      const roomId = `${getRepositoryName()}/${meta.id}`

      const { room, leave } = client.enterRoom(roomId, {
        initialStorage: {
          document: new LiveObject(initData.document as JsonObject),
        },
        autoConnect: false,
      })

      // init() is an interesting beast. It MUST be called from a render function
      // because we want to suspend on the room's storage availability before showing any UI so we need a reference to the room synchronously.
      // However, because this is called in render, we can't set our own state synchronously, lest we get a react warning.
      setTimeout(() => setRoomState({ room, leave }))

      return room
    },
    [client, roomState],
  )

  useEffect(() => {
    roomState?.room.connect()
    return () => roomState?.leave()
  }, [roomState])

  return (
    <LiveblocksRoomContext.Provider value={roomState?.room ?? null}>
      <initRoomContext.Provider value={init}>
        {children}
      </initRoomContext.Provider>
    </LiveblocksRoomContext.Provider>
  )
}

interface RoomState {
  room: Room
  leave: () => void
}

/**
 * Similar to Liveblock's useStorage but optional and not throwing.
 * Indeed, the presence of a room above the component hierarchy is optional for Page Builder.
 */
export function useCollaborationStorage() {
  try {
    const value = (useStorage((_) => _) as { document: DocumentT }) ?? undefined

    // This is actually safe. The try catch is here in case a collaboration room is not applicable in the current feature flag context.
    // If we change version and a room becomes available, this hook will be a brand new one nested inside a newly mounted CollaborationRoomInner parent.
    // If the room is not available, the first hook in the try catch will always throw. If the room is available, neither hook will ever throw.
    // Thus, that hook is not conditional.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const update = useMutation(({ storage }, localData: DocumentT) => {
      storage.set("document", new LiveObject(localData as LsonObject))
    }, [])

    return { value, update }
  } catch {
    return undefined
  }
}

/** Internal, don't use outside ExistingDocument. */
export function useInitCollaborationRoom() {
  return useContext(initRoomContext)
}

type InitRoomContextValue = (initData: ExistingDocumentData) => Room

const initRoomContext = createContext<InitRoomContextValue | undefined>(
  undefined,
)

declare global {
  interface Liveblocks {
    Storage: {
      // Note: We keep casting LsonObject from/to DocumentT.
      // DocumentT looks like a very complicated type, generated by io-ts but it IS compatible with a plain/serializable JSON/LSON type, in reality.
      document: LiveObject<LsonObject>
    }
  }
}
